import React from "react";
import Link from "next/link";
import { NewContainer } from "../NewContainer";

const features = [
  {
    icon: "robot",
    type: "ai",
    title: "AI-powered bookkeeping",
    description:
      "that automatically categorises, reconciles, and organises your transactions in real time.",
    subtitle: "Powered by AI · Verified by an accountant",
  },
  {
    icon: "user-tie",
    type: "human",
    title: "Expert accounting support and CPA/CA oversight",
    description:
      "available in your time zone — professionals who understand your reporting requirements.",
    subtitle: "Real people · Not chatbots",
  },
  {
    icon: "chart",
    type: "ai",
    title: "KPI dashboards",
    description:
      "generated automatically so you always know where your business stands.",
    subtitle: "AI-generated · Human-curated insights",
  },
  {
    icon: "sync",
    type: "ai",
    title: "Real-time reconciliation automatically",
    description:
      "syncing transactions across your financial systems.",
    subtitle: "99.2% AI accuracy · 100% human-verified",
  },
];

const FeatureIcon = ({ icon, type }) => {
  const bgColor =
    type === "ai" ? "bg-blue-primary-500/[0.08]" : "bg-textNoConnection/[0.08]";
  const textColor = type === "ai" ? "text-blue-primary-500" : "text-textNoConnection";

  const icons = {
    robot: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
      </svg>
    ),
    "user-tie": (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
          clipRule="evenodd"
        />
      </svg>
    ),
    chart: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm6-4a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
      </svg>
    ),
    sync: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" />
      </svg>
    ),
  };

  return (
    <div
      className={`w-[52px] h-[52px] min-w-[52px] rounded-[14px] flex items-center justify-center mt-0.5 ${bgColor} ${textColor}`}
    >
      {icons[icon]}
    </div>
  );
};

const PrimarySection = ({ featureRef }) => {
  return (
    <section
      ref={featureRef}
      id="services"
      className="py-[120px] px-6 lg:px-10 bg-base-white"
    >
      <div className="max-w-[1340px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-16 lg:gap-[100px] items-start">
        {/* Left: Sticky heading */}
        <div className="lg:sticky lg:top-[120px] fade-in">
          <div className="mb-4">
            <span className="inline-flex items-center gap-1.5 bg-blue-primary-500/[0.08] text-blue-primary-500 text-[13px] font-semibold px-4 py-1.5 rounded-full tracking-[0.5px]">
              Features
            </span>
          </div>
          <h2 className="font-inter text-[36px] leading-[46px] font-extrabold tracking-[-1.5px] mb-5">
            What you get with <span className="text-blue-primary-500">Ledgerowl</span>
          </h2>
          <p className="font-inter text-[17px] leading-[1.7] text-gray-cool-500 mb-7">
            AI-powered workflow automation with expert CA/CPA oversight.  Not bots pretending to be accountants — real financial expertise validating every result.
          </p>
          <Link
            href="#how-it-works"
            className="inline-flex items-center gap-2 bg-blue-primary-500 hover:bg-[#005bb5] text-white px-7 py-3.5 rounded-full text-base font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,116,230,0.35)]"
          >
            See how it works
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </Link>
        </div>

        {/* Right: Feature rows */}
        <div>
          {features.map((feature, i) => {
            // "Real people" feature gets a distinct card layout
            const isRealPeople = feature.subtitle.includes("Real people");
            if (isRealPeople) {
              return (
                <div
                  key={feature.title}
                  className="fade-in bg-blue-primary-50 rounded-2xl p-7 my-4"
                >
                  <div className="flex items-start gap-5">
                    <FeatureIcon icon={feature.icon} type={feature.type} />
                    <div className="flex-1">
                      <p className="font-inter text-[17px] font-medium leading-[1.55] text-gray-cool-900">
                        <strong>{feature.title}</strong> {feature.description}
                      </p>
                      <p className="text-[13px] font-normal text-gray-cool-500 mt-1">
                        {feature.subtitle}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div
                key={feature.title}
                className={`fade-in flex items-start gap-6 py-7 border-b border-gray-cool-300 ${
                  i === 0 ? "border-t" : ""
                }`}
              >
                <FeatureIcon icon={feature.icon} type={feature.type} />
                <div>
                  <p className="font-inter text-[17px] font-medium leading-[1.55] text-gray-cool-900">
                    <strong>{feature.title}</strong> {feature.description}
                  </p>
                  <p className="text-[13px] font-normal text-gray-cool-500 mt-1">
                    {feature.subtitle}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PrimarySection;
